import React, { useState, useEffect, useRef } from 'react';
import { bookings } from '../../api';
import LoadingSkeleton from '../LoadingSkeleton';
import SessionTypeIcon from './SessionTypeIcon';
import SessionTypeSelector from './SessionTypeSelector';
import JoinTypeWaitlistModal from './JoinTypeWaitlistModal';
import MyCoachingPacks from './MyCoachingPacks';
import SlotCourtInfo from './SlotCourtInfo';
import SlotHighlightsBanner from './SlotHighlightsBanner';
import BookingPartyPicker, { isPartyValid, maxPartyForSlot } from './BookingPartyPicker';
import SlotCalendar, { dayKey } from './SlotCalendar';
import './Bookings.css';
import LessonPrices from './LessonPrices';

export default function BrowseCoachingTab({ navTarget = null, user = null }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  // Required "who is joining" block - starts empty on purpose (no default count)
  // Banner tap: open the Browse list on that day with the slot's booking form ready
  const handlePickHighlight = (picked) => {
    setBrowseExpanded(true);
    setSelectedDay(dayKey(picked.startTime));
    const full = slots.find(s => s._id === picked._id);
    if (full) setSelectedSlot(full);
    setTimeout(() => {
      const el = document.getElementById(`slot-card-${picked._id}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 400);
  };

  const emptyParty = { count: '', players: [] };
  const [party, setParty] = useState(emptyParty);

  // A fresh slot selection always starts with a blank party
  useEffect(() => {
    setParty({ count: '', players: [] });
  }, [selectedSlot?._id]);
  const [requesting, setRequesting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchCoach, setSearchCoach] = useState('');
  const [selectedDay, setSelectedDay] = useState(null);
  // ✅ FEATURE 18: Add session type filtering state
  const [selectedSessionTypes, setSelectedSessionTypes] = useState([]);
  const [showSessionFilter, setShowSessionFilter] = useState(false);
  // ✅ FEATURE 2: Add type waitlist modal state
  const [showTypeWaitlistModal, setShowTypeWaitlistModal] = useState(false);
  const [selectedSlotForWaitlist, setSelectedSlotForWaitlist] = useState(null);
  // ✅ PHASE 1: Add collapse state for Browse section
  const [browseExpanded, setBrowseExpanded] = useState(false);
  // ✅ PHASE 2: Add My Bookings subsection state
  const [showMyBookings, setShowMyBookings] = useState(false);

  // Notification tap: open the section it is about and bring it into view
  useEffect(() => {
    if (!navTarget || navTarget.tab !== 'browse-coaching') return undefined;
    let anchor = null;
    if (navTarget.sub === 'my-bookings') {
      setShowMyBookings(true);
      anchor = 'player-my-bookings-anchor';
    } else if (navTarget.sub === 'browse') {
      setBrowseExpanded(true);
      anchor = 'player-browse-anchor';
    } else if (navTarget.sub === 'my-plans') {
      anchor = 'player-my-plans-anchor';
    }
    if (!anchor) return undefined;
    const timer = setTimeout(() => {
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('note-focus-flash');
        setTimeout(() => el.classList.remove('note-focus-flash'), 3500);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [navTarget]);
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsLoading, setMyBookingsLoading] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [canceling, setCanceling] = useState(null);

  // Upcoming sessions: the player's confirmed (and still-pending) lessons that
  // haven't started. Loaded as soon as the tab opens - and, only when there are
  // none, the Browse list below opens by itself so there is something to book.
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const autoExpandDone = useRef(false);

  useEffect(() => {
    loadMyBookings().finally(() => setBookingsLoaded(true));
  }, []);

  const upcomingSessions = myBookings
    .filter(b => ['approved', 'pending'].includes(b.status) && new Date(b.scheduledStartTime) > new Date())
    .sort((a, b) => new Date(a.scheduledStartTime) - new Date(b.scheduledStartTime));

  useEffect(() => {
    if (!bookingsLoaded || autoExpandDone.current) return;
    autoExpandDone.current = true;
    if (upcomingSessions.length === 0) setBrowseExpanded(true);
  }, [bookingsLoaded]);

  // ✅ Reload on session type filter change (removed page dependency for infinite scroll)
  useEffect(() => {
    loadSlots();
  }, [selectedSessionTypes]);

  // ✅ PHASE 2: Load My Bookings when section is expanded (independent of Browse)
  useEffect(() => {
    if (showMyBookings && myBookings.length === 0) {
      loadMyBookings();
    }
  }, [showMyBookings]);

  const loadSlots = async () => {
    try {
      setLoading(true);
      // No upper date bound - match CoachView's "Individual Slots" list
      // (MyAvailableSlotsTab), which shows every future slot with no cap.
      // A 90-day cap here (copied from the public guest-booking flow,
      // where it makes sense for anonymous visitors) meant a slot a coach
      // created further out was visible in CoachView but invisible to
      // players in this tab.
      const now = new Date();

      // ✅ Load ALL slots at once (no pagination) for infinite scroll experience
      // Use limit 1000 to get all available slots
      const response = await bookings.browseSlots(
        1,
        1000,
        null,
        now.toISOString(),
        null,
        selectedSessionTypes
      );
      setSlots(response.data.slots || []);
      setError(null);
    } catch (err) {
      console.error('❌ Error loading slots:', err);
      setError('Failed to load coaching slots');
    } finally {
      setLoading(false);
    }
  };

  // ✅ PHASE 2: Load user's booking requests
  const loadMyBookings = async () => {
    try {
      setMyBookingsLoading(true);
      const response = await bookings.getMyRequests();
      setMyBookings(response.data.bookings || []);
    } catch (err) {
      console.error('❌ Error loading my bookings:', err);
    } finally {
      setMyBookingsLoading(false);
    }
  };

  // ✅ PHASE 2: Handle booking cancellation
  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking? This action cannot be undone.')) {
      return;
    }

    setCanceling(bookingId);
    try {
      await bookings.cancelBooking(bookingId, 'Canceled by requester');
      setMyBookings(prevs =>
        prevs.map(b =>
          b._id === bookingId ? { ...b, status: 'canceled' } : b
        )
      );
    } catch (err) {
      console.error('❌ Error canceling booking:', err);
      alert('Failed to cancel booking');
    } finally {
      setCanceling(null);
    }
  };

  const handleRequestSlot = async () => {
    if (!selectedSlot) return;

    const maxPlayers = maxPartyForSlot(selectedSlot);
    if (!isPartyValid(party, maxPlayers)) {
      alert('Please enter how many players are joining and a name for each of them.');
      return;
    }

    setRequesting(true);
    try {
      await bookings.requestSlot(
        selectedSlot._id,
        requestMessage,
        Number(party.count),
        party.players.map(p => ({ name: p.name.trim(), userId: p.userId }))
      );
      setSuccessMessage('✅ Booking request sent! Awaiting coach approval.');
      setSelectedSlot(null);
      setRequestMessage('');

      // Reload slots to update UI
      setTimeout(() => {
        loadSlots();
        setSuccessMessage(null);
      }, 1500);
    } catch (err) {
      console.error('❌ Error requesting slot:', err);
      console.error('Full error response:', err.response?.data);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to request slot';
      alert(`Booking Error: ${errorMsg}`);
    } finally {
      setRequesting(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      meridiem: 'short'
    });
  };

  const formatTimeOnly = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      meridiem: 'short'
    });
  };

  const getDurationMinutes = (startTime, endTime) => {
    return Math.round((new Date(endTime) - new Date(startTime)) / 60000);
  };

  // ✅ PHASE 1: Only show slots that haven't started yet, soonest first
  const now = new Date();
  const activeSlots = slots
    .filter(slot => new Date(slot.startTime) > now)
    .sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

  // Calendar day filter for the list below
  const visibleSlots = selectedDay
    ? activeSlots.filter(slot => dayKey(slot.startTime) === selectedDay)
    : activeSlots;

  if (loading && slots.length === 0) {
    return (
      <div className="booking-tab-content">
        <div style={{ padding: '20px 0' }}>
          <LoadingSkeleton type="card" count={4} />
        </div>
      </div>
    );
  }

  return (
    <div className="booking-tab-content">
      {/* Player's own multi-session packs, with progress. Renders nothing
          if the player hasn't booked any. */}
      <div id="player-my-plans-anchor"><MyCoachingPacks /></div>

      {/* Coach's featured / last-minute slots this week */}
      <SlotHighlightsBanner onPick={handlePickHighlight} />

      {/* Upcoming sessions - what the player already has booked */}
      <div style={{ padding: '12px 16px 4px' }}>
        <div style={{ fontWeight: 700, fontSize: '15px', color: '#2c3e50', marginBottom: '8px' }}>
          📅 Upcoming Sessions{bookingsLoaded && upcomingSessions.length > 0 ? ` (${upcomingSessions.length})` : ''}
        </div>
        {!bookingsLoaded ? (
          <div style={{ fontSize: '13px', color: '#999', padding: '8px 0 12px' }}>Loading…</div>
        ) : upcomingSessions.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#7f8c8d', padding: '8px 0 12px' }}>No upcoming sessions</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {upcomingSessions.map(b => (
              <div
                key={b._id}
                className="booking-request-card"
                style={{ borderLeft: `4px solid ${b.status === 'approved' ? '#27ae60' : '#f39c12'}`, padding: '12px 14px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ fontWeight: 700, fontSize: '14px', color: '#2c3e50' }}>
                    {new Date(b.scheduledStartTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    {' · '}
                    {formatTimeOnly(b.scheduledStartTime)} - {formatTimeOnly(b.scheduledEndTime)}
                  </div>
                  <span style={{
                    flexShrink: 0, fontSize: '10px', fontWeight: 700, padding: '3px 8px', borderRadius: '999px',
                    textTransform: 'uppercase', letterSpacing: '0.4px',
                    background: b.status === 'approved' ? '#e6f7ed' : '#fffbf0',
                    color: b.status === 'approved' ? '#166534' : '#92400e'
                  }}>
                    {b.status === 'approved' ? 'Confirmed' : 'Awaiting approval'}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                  👨‍🏫 {b.coachId?.name || 'Coach'}{b.slotTitle ? ` • ${b.slotTitle}` : ''}
                  {b.partySize > 1 ? ` • 👥 ${b.partySize} players` : ''}
                </div>
                <SlotCourtInfo slot={b.slotId} margin="6px 0 0" />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ PHASE 1: Collapsible Browse Coaching Header */}
      <div
        id="player-browse-anchor"
        className="browse-header"
        onClick={() => setBrowseExpanded(!browseExpanded)}
        style={{
          padding: '12px 16px',
          cursor: 'pointer',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          borderBottom: '1px solid #e0e0e0',
          background: '#f5f5f5',
          borderRadius: '8px 8px 0 0',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: '18px', pointerEvents: 'none' }}>
          {browseExpanded ? '▼' : '▶'}
        </span>
        <span style={{ pointerEvents: 'none' }}>📚 Browse Coaching Sessions ({activeSlots.length})</span>
      </div>

      {/* ✅ PHASE 1: Browse section content - only visible when expanded */}
      {browseExpanded && (
        <div className="browse-content">
          <LessonPrices />
          <SlotCalendar slots={activeSlots} selectedDay={selectedDay} onSelectDay={setSelectedDay} />

          {/* ✅ FEATURE 18: Add filter header with toggle button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '15px',
            padding: '12px 16px 0 16px'
          }}>
            <h3 style={{ margin: 0 }}>
              Filter Sessions
            </h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={() => setShowSessionFilter(!showSessionFilter)}
                style={{
                  padding: '6px 12px',
                  background: selectedSessionTypes.length > 0 ? '#3498db' : '#ecf0f1',
                  color: selectedSessionTypes.length > 0 ? 'white' : '#4b5563',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <span>⚙️ Filter</span>
                {selectedSessionTypes.length > 0 && (
                  <span style={{
                    background: '#e74c3c',
                    color: 'white',
                    borderRadius: '10px',
                    width: '18px',
                    height: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    {selectedSessionTypes.length}
                  </span>
                )}
              </button>

              {/* ✅ Counter showing total available slots */}
              <div style={{
                padding: '6px 12px',
                background: '#e6f7ed',
                color: '#166534',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                📅 {activeSlots.length} available
              </div>
            </div>
          </div>

      {/* ✅ FEATURE 18: Filter panel (conditionally shown) */}
      {showSessionFilter && (
        <div style={{
          padding: '15px',
          background: '#f8f9fa',
          borderRadius: '6px',
          marginBottom: '15px',
          border: '1px solid #ddd'
        }}>
          <SessionTypeSelector
            selectedTypes={selectedSessionTypes}
            onChange={(types) => {
              setSelectedSessionTypes(types);
            }}
            showLabel={true}
          />
        </div>
      )}

          {successMessage && (
            <div style={{
              padding: '12px',
              background: '#e6f7ed',
              border: '1px solid #27ae60',
              borderRadius: '6px',
              color: '#229954',
              marginBottom: '15px',
              fontSize: '13px',
              margin: '12px 16px 15px 16px'
            }}>
              {successMessage}
            </div>
          )}

          {error && (
            <div style={{
              padding: '12px',
              background: '#ffe6e6',
              border: '1px solid #e74c3c',
              borderRadius: '6px',
              color: '#c0392b',
              marginBottom: '15px',
              fontSize: '13px',
              margin: '12px 16px 15px 16px'
            }}>
              ⚠️ {error}
            </div>
          )}

          {visibleSlots.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>📭</div>
              No coaching sessions available right now
              <br />
              <span style={{ fontSize: '14px' }}>Check back later or ask your coach to offer sessions</span>
            </div>
          ) : (
            <>
              <div
                key={selectedDay || 'all'}
                className={`booking-slots-list ${selectedDay ? 'booking-slots-list--focus' : ''}`}
                style={{ padding: '0 16px' }}
              >
                {visibleSlots
                  .reduce((unique, slot) => {
                    if (!unique.some(s => s._id === slot._id)) {
                      unique.push(slot);
                    }
                    return unique;
                  }, [])
                  .map((slot, slotIndex) => (
              <div
                key={slot._id}
                id={`slot-card-${slot._id}`}
                className={`booking-slot-card ${selectedDay ? 'slot-card-enter' : ''}`}
                style={{
                  animationDelay: selectedDay ? `${slotIndex * 90}ms` : undefined,
                  padding: 0,
                  overflow: 'hidden',
                  border: '1px solid rgba(47, 75, 162, 0.14)',
                  borderRadius: '14px',
                  boxShadow: selectedSlot?._id === slot._id
                    ? '0 0 0 2px #2f4ba2, 0 10px 24px rgba(47, 75, 162, 0.22)'
                    : undefined,
                  background: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={() => setSelectedSlot(selectedSlot?._id === slot._id ? null : slot)}
              >
                {/* DATE & TIME - the one thing this card leads with. Full-
                    bleed banner (the card's own padding:0 + overflow:hidden
                    let it span edge-to-edge without the old negative-margin
                    workaround), nothing else competing for attention here -
                    duration/coach/booked-count live in the meta line below. */}
                <div style={{
                  background: 'linear-gradient(135deg, #2f4ba2 0%, #14246b 100%)',
                  color: 'white',
                  padding: '16px 16px 15px',
                  textAlign: 'center',
                  letterSpacing: '0.2px'
                }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', lineHeight: 1.25 }}>
                    {new Date(slot.startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '500', opacity: 0.92, marginTop: '3px', fontVariantNumeric: 'tabular-nums' }}>
                    {formatTimeOnly(slot.startTime)} - {formatTimeOnly(slot.endTime)}
                  </div>
                </div>

                <div style={{ padding: '16px 18px 18px' }}>
                  <div className="booking-slot-header" style={{ gap: '12px' }}>
                    <h4 style={{ margin: 0, color: '#2c3e50', fontSize: '16px', fontWeight: '650', lineHeight: 1.3, flex: 1 }}>
                      {slot.title}
                    </h4>

                    {slot.currentBookings >= slot.maxBookings ? (
                      <span style={{
                        flexShrink: 0,
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '10.5px',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        background: '#ffe6e6',
                        color: '#c0392b',
                        textTransform: 'uppercase'
                      }}>
                        🔴 Full
                      </span>
                    ) : (
                      <span style={{
                        flexShrink: 0,
                        padding: '4px 10px',
                        borderRadius: '999px',
                        fontSize: '10.5px',
                        fontWeight: '700',
                        letterSpacing: '0.5px',
                        background: '#e6f7ed',
                        color: '#166534',
                        textTransform: 'uppercase'
                      }}>
                        🟢 Available
                      </span>
                    )}
                  </div>

                  {/* Meta line - coach, duration, booked count, all together */}
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 10px',
                    alignItems: 'center',
                    fontSize: '12px',
                    color: '#7f8c8d',
                    marginTop: '8px'
                  }}>
                    <span>👨‍🏫 {slot.coachId?.name || 'Coach'}</span>
                    <span>⏱️ {getDurationMinutes(slot.startTime, slot.endTime)} min</span>
                    <span style={{ fontWeight: '600', color: '#2c3e50' }}>
                      {slot.currentBookings}/{slot.maxBookings} booked
                    </span>
                  </div>

                  {(slot.sessionType || (slot.tags && slot.tags.length > 0)) && (
                    <div style={{ display: 'flex', gap: '6px', marginTop: '10px', flexWrap: 'wrap' }}>
                      {slot.sessionType && (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          padding: '3px 9px',
                          borderRadius: '20px',
                          background: '#eef1ff',
                          color: '#5a4fcf',
                          textTransform: 'uppercase',
                          letterSpacing: '0.4px'
                        }}>
                          {slot.sessionType}
                        </span>
                      )}
                      {slot.tags && slot.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          style={{
                            fontSize: '11px',
                            padding: '3px 9px',
                            background: '#ecf0f1',
                            borderRadius: '20px',
                            color: '#34495e'
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <SlotCourtInfo slot={slot} margin="10px 0 0" />

                {/* Full slot warning and Join Waitlist button - ALWAYS VISIBLE */}
                {slot.currentBookings >= slot.maxBookings && (
                  <div onClick={(e) => e.stopPropagation()} style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ background: '#fff3cd', padding: '10px', borderRadius: '4px', fontSize: '12px', color: '#856404' }}>
                      <strong>📋 This slot is full!</strong>
                      <br />
                      Join the waitlist for {slot.sessionType ? `${slot.sessionType} coaching` : 'this type of coaching'} and we'll notify you when a spot opens.
                    </div>
                    <button
                      onClick={() => {
                        setSelectedSlotForWaitlist(slot);
                        setShowTypeWaitlistModal(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        background: '#f39c12',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      📋 Join Waitlist
                    </button>
                  </div>
                )}

                {/* Expanded request/waitlist form */}
                {selectedSlot?._id === slot._id && (
                  <div
                  // The whole card toggles open/closed on tap; taps inside the open
                  // form (party picker, message, buttons) must not bubble up and
                  // collapse it, or none of it can be used.
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginTop: '15px',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}>
                    <div style={{ fontSize: '13px', color: '#2c3e50' }}>
                      <strong>📅 Time:</strong> {formatTime(slot.startTime)} ({getDurationMinutes(slot.startTime, slot.endTime)} min)
                      <br />
                      <strong>👨‍🏫 Coach:</strong> {slot.coachId?.name || 'Coach'}
                    </div>

                    {/* Rating requirements (if applicable) */}
                    {slot.sessionType && slot.sessionType !== 'mental' && slot.sessionType !== 'custom' && (
                      <div style={{ fontSize: '12px', color: '#8e44ad', background: '#f4e9ff', padding: '8px', borderRadius: '4px', marginTop: '8px' }}>
                        📊 <strong>Skill Matching:</strong> This is a {slot.sessionType} lesson
                        <br />
                        <span style={{ fontSize: '11px' }}>You'll be matched with players at similar skill levels</span>
                      </div>
                    )}

                    {slot.currentBookings < slot.maxBookings && (
                      <BookingPartyPicker
                        value={party}
                        onChange={setParty}
                        maxPlayers={maxPartyForSlot(slot)}
                        lessonMinutes={getDurationMinutes(slot.startTime, slot.endTime)}
                        ownerName={user?.name || ''}
                        disabled={requesting}
                      />
                    )}

                    {slot.currentBookings < slot.maxBookings && (
                      // Request message textarea (only for available slots)
                      <textarea
                        value={requestMessage}
                        onChange={(e) => setRequestMessage(e.target.value)}
                        placeholder="Optional: Tell the coach why you'd like this session (e.g., 'I want to work on my backhand')"
                        maxLength="500"
                        rows="3"
                        style={{
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontFamily: 'inherit',
                          fontSize: '13px'
                        }}
                      />
                    )}

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => {
                          setSelectedSlot(null);
                          setRequestMessage('');
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#95a5a6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Cancel
                      </button>
                      {slot.currentBookings >= slot.maxBookings ? (
                        // ✅ FEATURE 2: Join waitlist button
                        <button
                          onClick={() => {
                            setSelectedSlotForWaitlist(slot);
                            setShowTypeWaitlistModal(true);
                            setSelectedSlot(null);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: '#f39c12',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 'bold'
                          }}
                        >
                          📋 Join Waitlist
                        </button>
                      ) : (
                        // Request coaching button (only for available slots)
                        <button
                          onClick={handleRequestSlot}
                          disabled={requesting || !isPartyValid(party, maxPartyForSlot(slot))}
                          title={isPartyValid(party, maxPartyForSlot(slot)) ? '' : 'Enter how many players are joining and their names'}
                          style={{
                            padding: '8px 16px',
                            background: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: (requesting || !isPartyValid(party, maxPartyForSlot(slot))) ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            opacity: (requesting || !isPartyValid(party, maxPartyForSlot(slot))) ? 0.6 : 1
                          }}
                        >
                          {requesting ? '⏳ Requesting...' : '✅ Request Coaching'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
                </div>
              </div>
            ))}
          </div>
            </>
          )}
        </div>
      )}

      {/* ✅ PHASE 2: My Bookings Subsection - ALWAYS VISIBLE, COLLAPSED BY DEFAULT */}
      <div style={{
        marginTop: '0px',
        borderTop: '2px solid #e0e0e0',
        paddingTop: '0px'
      }} id="player-my-bookings-anchor">
        <div
          onClick={() => setShowMyBookings(!showMyBookings)}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '14px',
            userSelect: 'none',
            background: '#f5f5f5',
            borderRadius: '8px'
          }}
        >
          <span style={{ fontSize: '18px', pointerEvents: 'none' }}>
            {showMyBookings ? '▼' : '▶'}
          </span>
          <span style={{ pointerEvents: 'none' }}>📋 My Booking Requests</span>
        </div>

        {showMyBookings && (
          <div style={{ padding: '12px 16px', paddingTop: '12px' }}>
            {myBookingsLoading ? (
              <div style={{ padding: '12px 0' }}>
                <LoadingSkeleton type="card" count={2} />
              </div>
            ) : myBookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
                📭 No booking requests yet
              </div>
            ) : (
              <div className="booking-requests-list">
                {myBookings.map(booking => (
                  <div
                    key={booking._id}
                    className="booking-request-card"
                    style={{
                      borderLeft: `4px solid ${
                        booking.status === 'pending' ? '#f39c12' :
                        booking.status === 'approved' ? '#27ae60' :
                        booking.status === 'denied' ? '#e74c3c' : '#95a5a6'
                      }`,
                      background: expandedBooking === booking._id ? '#f0f7ff' : 'white',
                      cursor: 'pointer',
                      marginBottom: '12px'
                    }}
                    onClick={() => setExpandedBooking(expandedBooking === booking._id ? null : booking._id)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          color: '#2c3e50',
                          marginBottom: '5px'
                        }}>
                          {booking.slotTitle}
                        </div>
                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '5px' }}>
                          👨‍🏫 {booking.coachId?.name || 'Coach'} • 🕐 {new Date(booking.scheduledStartTime).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </div>
                        <SlotCourtInfo slot={booking.slotId} margin="0 0 5px" />
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        background:
                          booking.status === 'pending' ? '#fffbf0' :
                          booking.status === 'approved' ? '#f0f7f0' :
                          booking.status === 'denied' ? '#fef0f0' : '#f5f5f5',
                        color:
                          booking.status === 'pending' ? '#92400e' :
                          booking.status === 'approved' ? '#166534' :
                          booking.status === 'denied' ? '#991b1b' : '#4b5563',
                        textTransform: 'uppercase'
                      }}>
                        {booking.status === 'pending' ? '⏳ Pending' :
                         booking.status === 'approved' ? '✅ Approved' :
                         booking.status === 'denied' ? '❌ Denied' : '⛔ Canceled'}
                      </span>
                    </div>

                    {/* Expanded details */}
                    {expandedBooking === booking._id && (
                      <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #ecf0f1' }}>
                        {booking.message && (
                          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
                            💬 {booking.message}
                          </div>
                        )}
                        {booking.responseMessage && (
                          <div style={{ fontSize: '12px', color: '#e74c3c', marginBottom: '8px' }}>
                            📝 Coach response: {booking.responseMessage}
                          </div>
                        )}
                        {booking.status === 'pending' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelBooking(booking._id);
                            }}
                            disabled={canceling === booking._id}
                            style={{
                              padding: '6px 12px',
                              background: '#e74c3c',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: canceling === booking._id ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              fontWeight: '600',
                              opacity: canceling === booking._id ? 0.6 : 1
                            }}
                          >
                            {canceling === booking._id ? '⏳ Canceling...' : '❌ Cancel Request'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Type Waitlist Modal */}
      <JoinTypeWaitlistModal
        isOpen={showTypeWaitlistModal}
        onClose={() => setShowTypeWaitlistModal(false)}
        coachId={selectedSlotForWaitlist?.coachId?._id || selectedSlotForWaitlist?.coachId}
        onSuccess={() => {
          setShowTypeWaitlistModal(false);
          loadSlots();
        }}
      />
    </div>
  );
}
